import { ERROR_CODE_LABELS } from '../core/errors';
import type { ConverterCapabilities, ConverterMode, FidelityLevel } from '../core/types';
import type { WorkbenchState } from '../app/hooks/useConversions';
import { FidelityReport } from './FidelityReport';
import { FormatSelect, type FormatOption } from './FormatSelect';

export interface TaskPanelProps {
  state: WorkbenchState;
  options: FormatOption[];
  /** 当前「源 → 目标」的候选通道（本地 / AI），来自能力注册表。 */
  channels: ConverterCapabilities[];
  /** 服务端权威单价（来自 /api/account/usage）；为空时退化为能力声明的预估。 */
  pricing: { localCost: number; aiCost: number } | null;
  authenticated: boolean;
  /** 登录用户余额（未登录为 null） */
  balance: number | null;
  /** 未登录今日剩余免费次数（登录用户不适用） */
  freeRemaining: number | null;
  aiConsent: boolean;
  onAiConsentChange: (value: boolean) => void;
  onChannelChange: (mode: ConverterMode) => void;
  quality: number;
  onQualityChange: (quality: number) => void;
  onTargetChange: (format: string) => void;
  onStart: () => void;
  onCancel: () => void;
  onDownload: () => void;
  onReset: () => void;
}

const LOSSLESS_TARGETS = new Set(['png']);

export function TaskPanel(props: TaskPanelProps) {
  const { state } = props;
  if (!state.file || !state.metadata) {
    return (
      <div className="card">
        <div className="card-head">
          <span className="card-title">转换台</span>
          <span className="spacer" />
          <span className="card-hint">选择文件后在此设定目标格式</span>
        </div>
        <div className="empty">
          还没有待转换的文件。
          <br />
          左侧拖入文件即可开始：默认走本地通道（不上传源文件、每次 1 分）；扫描件与图片可选 AI 识别（2 分，需登录）。
        </div>
      </div>
    );
  }

  const detection = state.metadata.detection;
  const mismatch = detection ? !detection.extensionMatchesContent : false;
  const converting = state.status === 'converting';
  const canDownload = state.status === 'done' && !!state.result?.verificationPassed;

  const activeChannel = props.channels.find((channel) => channel.mode === state.channel) ?? props.channels[0] ?? null;
  const requiresUpload = activeChannel?.requiresUpload ?? false;
  const costOf = (mode: ConverterMode, fallback: number): number =>
    props.pricing ? (mode === 'local' ? props.pricing.localCost : props.pricing.aiCost) : fallback;
  const cost = activeChannel ? costOf(activeChannel.mode, activeChannel.estimatedCost) : null;

  const consentMissing = requiresUpload && !props.aiConsent;
  const insufficient = props.authenticated && props.balance !== null && cost !== null && props.balance < cost;
  const freeExhausted = !props.authenticated && props.freeRemaining === 0;
  const canStart = Boolean(state.targetFormat) && !converting && !consentMissing && !insufficient && !freeExhausted;

  return (
    <div className="card stack">
      <div className="card-head">
        <span className="card-title">转换台</span>
        <span className="spacer" />
        <span className="badge" data-tone={requiresUpload ? 'warn' : 'brand'}>
          {requiresUpload ? 'AI 识别 · 内容上传' : '本地执行'}
        </span>
      </div>

      <div className="file-row">
        {state.thumbnailUrl ? (
          <img className="thumb" src={state.thumbnailUrl} alt="源文件缩略图" />
        ) : (
          <div className="thumb-fallback">{state.metadata.type.extension.slice(0, 4).toUpperCase()}</div>
        )}
        <div style={{ minWidth: 0 }}>
          <div className="file-name" title={state.file.name}>
            {state.file.name}
          </div>
          <div className="file-meta">
            <span>{formatBytes(state.file.size)}</span>
            <span>·</span>
            <span>实际格式 {state.metadata.type.extension.toUpperCase()}</span>
            <span>·</span>
            <span>由文件头判定</span>
          </div>
          {state.hash ? (
            <div className="file-meta">
              <span className="mono">
                SHA-256 {state.hash.value.slice(0, 16)}…{state.hash.partial ? '（部分哈希）' : ''}
              </span>
            </div>
          ) : null}
        </div>
        <span className="spacer" />
        <button type="button" className="btn btn-ghost" onClick={props.onReset} disabled={converting}>
          移除
        </button>
      </div>

      {mismatch ? (
        <div className="notice" data-tone="warn">
          <span>⚠</span>
          <span>{detection?.warnings[0] ?? '扩展名与文件实际格式不一致，已按文件头判定结果处理。'}</span>
        </div>
      ) : null}

      <FormatSelect
        value={state.targetFormat}
        options={props.options}
        disabled={converting}
        onChange={props.onTargetChange}
      />

      {props.channels.length > 1 ? (
        <div className="field">
          <label>执行通道</label>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {props.channels.map((channel) => (
              <button
                key={channel.id}
                type="button"
                className={state.channel === channel.mode ? 'btn btn-primary' : 'btn'}
                disabled={converting}
                onClick={() => props.onChannelChange(channel.mode)}
              >
                {channel.mode === 'local'
                  ? `本地解析 · ${costOf('local', channel.estimatedCost)} 分`
                  : `AI 识别 · ${costOf('ai', channel.estimatedCost)} 分`}
              </button>
            ))}
          </div>
          <span className="card-hint">
            {requiresUpload
              ? 'AI 识别会渲染页面图片并上传，由第三方多模态模型转写；识别结果建议逐字校对。'
              : '本地通道在浏览器内完成，只上报文件名/大小/格式用于记账，源文件内容不出设备。'}
          </span>
        </div>
      ) : null}

      {requiresUpload ? (
        <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={props.aiConsent}
            disabled={converting}
            onChange={(event) => props.onAiConsentChange(event.target.checked)}
          />
          <span className="card-hint">
            我了解：AI 识别会把该文件的页面图片上传到本站服务器，并发送给第三方多模态模型（agnes / sensenova）用于文字转写；
            本地通道则完全不上传内容。
          </span>
        </label>
      ) : null}

      {!LOSSLESS_TARGETS.has(state.targetFormat) && state.metadata.type.category === 'image' && !requiresUpload ? (
        <div className="field">
          <label htmlFor="quality">输出质量 {Math.round(props.quality * 100)}%</label>
          <input
            id="quality"
            type="range"
            min={40}
            max={100}
            step={1}
            value={Math.round(props.quality * 100)}
            disabled={converting}
            onChange={(event) => props.onQualityChange(Number(event.target.value) / 100)}
          />
        </div>
      ) : null}

      <div className="detail-row">
        <span className="key">本次消耗</span>
        <span>
          {cost === null ? '—' : props.authenticated ? `${cost} 分` : `免费（今日剩余 ${props.freeRemaining ?? '—'} 次）`}
          {props.authenticated && props.balance !== null ? ` · 当前余额 ${props.balance} 分` : ''}
        </span>
      </div>

      {insufficient ? (
        <div className="notice" data-tone="warn">
          <span>⚠</span>
          <span>积分不足：本地转换每次 {props.pricing?.localCost ?? 1} 分、AI 识别每次 {props.pricing?.aiCost ?? 2} 分。每日额度会在当天首次使用时自动发放，也可在记录页反馈。</span>
        </div>
      ) : null}

      {freeExhausted ? (
        <div className="notice" data-tone="warn">
          <span>⚠</span>
          <span>今日 5 次免费额度已用完：登录 YoGo 账号后可按积分继续（登录用户每天还有额度发放）。</span>
        </div>
      ) : null}

      {converting ? (
        <div className="stack" style={{ gap: 8 }}>
          <div className="stage">
            <span>{state.stage || '转换中'}</span>
            <span>{state.progress}%</span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${state.progress}%` }} />
          </div>
          <div className="row">
            <span className="card-hint">取消后会自动退还本次扣费。</span>
            <span className="spacer" />
            <button type="button" className="btn" onClick={props.onCancel}>
              取消
            </button>
          </div>
        </div>
      ) : (
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={props.onStart} disabled={!canStart}>
            开始转换
          </button>
          <span className="card-hint">
            {consentMissing
              ? '请先勾选隐私确认'
              : cost === null
                ? '选择目标格式后开始'
                : props.authenticated
                  ? `将扣除 ${cost} 分`
                  : '将使用 1 次免费额度'}
          </span>
          <span className="spacer" />
          {state.status === 'done' ? (
            <>
              <span className="badge pulse" data-tone="ok">
                已完成 · {state.durationMs}ms
              </span>
              <button type="button" className="btn btn-primary" onClick={props.onDownload} disabled={!canDownload}>
                下载产物
              </button>
            </>
          ) : null}
        </div>
      )}

      {state.error ? (
        <div className="notice" data-tone="danger">
          <span>⛔</span>
          <span>
            <strong>{ERROR_CODE_LABELS[state.error.code as keyof typeof ERROR_CODE_LABELS] ?? '转换失败'}</strong>
            <br />
            {state.error.message}
            {state.error.suggestion ? <><br />{state.error.suggestion}</> : null}
          </span>
        </div>
      ) : null}

      {state.result ? (
        <>
          <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '4px 0' }} />
          <FidelityReport
            fidelity={state.result.fidelity}
            verificationPassed={state.result.verificationPassed}
            verificationDetails={state.result.verificationDetails}
          />
          <div className="detail-row">
            <span className="key">产物</span>
            <span>
              {state.result.fileName} · {formatBytes(state.result.blob.size)}
            </span>
          </div>
          {state.cost !== null ? (
            <div className="detail-row">
              <span className="key">消耗</span>
              <span>{state.cost > 0 ? `${state.cost} 分` : '1 次免费额度'}</span>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function fidelityBadge(level: FidelityLevel, summary: string) {
  return (
    <span className="badge" data-level={level} title={summary}>
      {level} · {summary}
    </span>
  );
}
